import { Easing, MaxSpeed, ResetDelay, WindowLength } from './constants.js';
import type { State, UseZoomWheel } from './types.js';
import { clamp } from '@studio/js-utils/range';
import { Range, Threshold } from '@studio/models/ptz';
import { useLatestFn, useLazyRef, useUnmount, χref } from '@studio/react-utils/hooks';
import type { WheelEvent } from 'react';
import { useState } from 'react';

const ALPHA = 2 / (WindowLength + 1);

/**
 * Hook to map scroll wheel movement to its corresponding zoom intent.
 *
 * @returns A tuple containing the zoom intent and a callback to be invoked on wheel events.
 */
export const useZoomWheel = (): UseZoomWheel => {
  const [zoomIntent, setZoomIntent] = useState(0);

  const stateRef = useLazyRef<State>(() => ({
    expMovingAvg: 0,
    lastTime: undefined,
    timeoutId: undefined
  }));

  // Stop interval on unmount.
  useUnmount(() => {
    const { timeoutId } = χref(stateRef);

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      stateRef.current.timeoutId = undefined;
    }
  });

  const takeSample = useLatestFn((ev: WheelEvent) => {
    const { deltaY } = ev;
    const currentTime = Date.now();
    const state = stateRef.current;

    if (state.lastTime) {
      const deltaTime = currentTime - state.lastTime;
      const currentSpeed = (deltaY / (deltaTime + 1)) * -1;

      // Calculate rolling EMA (thanks ChatGPT!)
      state.expMovingAvg = ALPHA * currentSpeed + (1 - ALPHA) * state.expMovingAvg;

      const { expMovingAvg } = state;

      const abs = Math.abs(expMovingAvg);
      const sign = Math.sign(expMovingAvg);

      const nonZeroAvg = Math.max(abs, Threshold) * sign;
      const magnitude = clamp((Math.abs(nonZeroAvg) / MaxSpeed) * sign, Range);

      setZoomIntent(Easing(Math.abs(magnitude)) * sign);
    }

    state.lastTime = currentTime;

    // Clear existing timeout.
    if (state.timeoutId) {
      window.clearTimeout(state.timeoutId);
    }

    // Install new timeout.
    state.timeoutId = window.setTimeout(() => {
      setZoomIntent(0);
      state.expMovingAvg = 0;
    }, ResetDelay);
  });

  return [zoomIntent, takeSample];
};
